var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import * as UnlinkedApplicationForm from "../../../components/UnlinkedApplicationForm";
import { AutoEntity, removeAutoEntity } from "../unlinkedApplicationSlice";
import { allVehiclesHaveData, isVehicleDataValid, } from "../../../utils/validateUnlinkedVehiclesData";
import { clickedAddButton, clickedDeleteButton, clickedEditButton, completedPage, viewedWidgetPage, } from "../../../lib/Avo";
import Badge from "../../../components/Badge";
import { Button } from "@savvyinsure/react-ui";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import { EXPERIENCE } from "../../../constants";
import Image from "../../../components/Image";
import { PAGES } from "../../../lib/TrellisAnalytics";
import { PHASE } from "../../../routeConfig";
import { SavvyOwnershipStatus } from "@savvyinsure/savvy-auto-policy/lib/enums";
import getVehiclePrimaryText from "./getVehiclePrimaryText";
import getVehicleSecondaryText from "./getVehicleSecondaryText";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { useDidMount } from "rooks";
import useEventPublisher from "../../../hooks/useEventPublisher";
import { useHistory } from "react-router-dom";
import { v4 as uuid } from "uuid";
var typeOfEntity = AutoEntity.Vehicle;
var vehicleEditPages = {
    year: PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_YEAR,
    make: PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_MAKE,
    model: PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_MODEL,
    trim: PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_TRIM,
    ownershipStatus: PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_STATUS,
    fullCoverage: PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_FULL_COVERAGE,
};
function getInvalidVehicleEditPage(vehicle, checkFullCoverage) {
    var _a, _b, _c, _d, _e;
    if (checkFullCoverage === void 0) { checkFullCoverage = false; }
    if (!((_a = vehicle.year) === null || _a === void 0 ? void 0 : _a.name)) {
        return vehicleEditPages.year;
    }
    if (!((_b = vehicle.make) === null || _b === void 0 ? void 0 : _b.name)) {
        return vehicleEditPages.make;
    }
    if (!((_c = vehicle.model) === null || _c === void 0 ? void 0 : _c.name)) {
        return vehicleEditPages.model;
    }
    if (!((_d = vehicle.trim) === null || _d === void 0 ? void 0 : _d.vin) && !((_e = vehicle.trim) === null || _e === void 0 ? void 0 : _e.vinStub)) {
        return vehicleEditPages.trim;
    }
    if (!vehicle.ownershipStatus) {
        return vehicleEditPages.ownershipStatus;
    }
    if (checkFullCoverage &&
        vehicle.ownershipStatus === SavvyOwnershipStatus.Owned &&
        vehicle.fullCoverage === undefined) {
        return vehicleEditPages.fullCoverage;
    }
    return vehicleEditPages.year;
}
export default function VehiclesListing() {
    var push = useHistory().push;
    var dispatch = useAppDispatch();
    var _a = React.useState(""), vehicleIdToRemove = _a[0], setVehicleIdToRemove = _a[1];
    var _b = React.useState(false), confirmationDialogOpen = _b[0], setConfirmationDialogOpen = _b[1];
    var isPrefill = useAppSelector(function (state) { return state.configuration.experience === EXPERIENCE.PREFILL; });
    var vehicles = useAppSelector(function (_a) {
        var auto = _a.unlinkedApplication.auto;
        return auto.vehicles;
    });
    var editId = useAppSelector(function (_a) {
        var vehicles = _a.unlinkedApplication.auto.entities.vehicles;
        return vehicles.editId;
    });
    var checkFullCoverage = useAppSelector(function (_a) {
        var auto = _a.unlinkedApplication.auto;
        return auto.checkFullCoverage;
    });
    var commonAnalyticsProperties = useAppSelector(function (state) { return state.analytics.commonAnalyticsProperties; });
    var eventPublisher = useEventPublisher();
    var shouldCheckFullCoverage = !isPrefill && checkFullCoverage;
    var invalidVehicle = vehicles.find(function (vehicle) { return vehicle.id === editId; });
    var hasOneVehicle = vehicles.length === 1;
    var invalidVehicleEditPage = invalidVehicle
        ? getInvalidVehicleEditPage(invalidVehicle, shouldCheckFullCoverage)
        : undefined;
    var someVehicleHasMissingData = !allVehiclesHaveData(vehicles, {
        checkOwnedFullCoverage: shouldCheckFullCoverage,
        isPrefill: isPrefill,
    });
    var nextRoute = PHASE.UNLINKED_APPLICATION_AUTO_DRIVERS;
    function handleCancelConfirmationDialog() {
        setConfirmationDialogOpen(false);
        setVehicleIdToRemove("");
    }
    function handleRemoveConfirmationDialog() {
        clickedDeleteButton(__assign(__assign({}, commonAnalyticsProperties), { typeOfEntity: typeOfEntity }));
        dispatch(removeAutoEntity({ entity: typeOfEntity, id: vehicleIdToRemove }));
        setConfirmationDialogOpen(false);
    }
    function handleEditVehicle(editPath, id) {
        clickedEditButton(__assign(__assign({}, commonAnalyticsProperties), { typeOfEntity: typeOfEntity }));
        push(editPath.replace(":vehicleId", id));
    }
    useDidMount(function () {
        viewedWidgetPage(__assign(__assign({}, commonAnalyticsProperties), { segmentPageName_: PAGES.UNLINKED_APPLICATION_AUTO_VEHICLES_REVIEW }));
        eventPublisher.transitionView(PAGES.UNLINKED_APPLICATION_AUTO_VEHICLES_REVIEW);
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("header", { className: "mb-4 flex items-center pt-6" },
            React.createElement(UnlinkedApplicationForm.Header, { className: "grow text-xl font-bold" }, "Your vehicles")),
        React.createElement("div", { className: "flex flex-col" },
            React.createElement("ul", { className: "mb-6 space-y-2" }, vehicles.map(function (vehicle) {
                var make = vehicle.make, model = vehicle.model, year = vehicle.year, trim = vehicle.trim, id = vehicle.id;
                var missingData = !isVehicleDataValid(vehicle, {
                    checkOwnedFullCoverage: shouldCheckFullCoverage,
                    isPrefill: isPrefill,
                });
                var editPath = isPrefill
                    ? getInvalidVehicleEditPage(vehicle, shouldCheckFullCoverage)
                    : PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_YEAR;
                return (React.createElement(UnlinkedApplicationForm.ListItemWithActionButtons, { key: id, icon: React.createElement(Image, { width: "24", height: "24", src: "/assets/icons/car-blue.svg" }), primaryText: getVehiclePrimaryText({ make: make, model: model }), secondaryText: getVehicleSecondaryText({ year: year, trim: trim }), tertiaryText: missingData ? React.createElement(Badge, { text: "Missing information" }) : null, onClick: missingData
                        ? function () { return handleEditVehicle(editPath, id); }
                        : undefined, actionButtons: [
                        React.createElement(UnlinkedApplicationForm.ListItemActionButton, { key: "edit", "data-testid": "edit-vehicle-action-button", icon: React.createElement(Image, { width: "18", height: "18", className: "max-w-none", src: "/assets/icons/pencil.svg" }), onClick: function () {
                                handleEditVehicle(editPath, id);
                            } }),
                        React.createElement(UnlinkedApplicationForm.ListItemActionButton, { key: "remove", isVisible: vehicles.length > 1, icon: React.createElement(Image, { width: "24", height: "24", className: "max-w-none", src: "/assets/icons/cross-mark.svg" }), onClick: function () {
                                setVehicleIdToRemove(id);
                                setConfirmationDialogOpen(true);
                            } }),
                    ] }));
            })),
            React.createElement("button", { onClick: function () {
                    clickedAddButton(__assign(__assign({}, commonAnalyticsProperties), { typeOfEntity: typeOfEntity }));
                    push(PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_YEAR.replace(":vehicleId", uuid()));
                }, className: "mx-auto inline-flex items-center rounded-[48px] border-2 border-savvy p-4 text-base font-bold uppercase leading-none text-savvy" },
                React.createElement("svg", { width: "17", height: "16", viewBox: "0 0 17 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                    React.createElement("path", { d: "M8.5 2.66699V8.00048M8.5 13.334V8.00048M8.5 8.00048H3.16651M8.5 8.00048H13.8335", stroke: "currentColor", strokeWidth: "1.33333" }),
                    React.createElement("path", { d: "M8.5 2.66699V8.00048M8.5 13.334V8.00048M8.5 8.00048H3.16651M8.5 8.00048H13.8335", stroke: "currentColor", strokeWidth: "1.33333" })),
                React.createElement("span", { className: "ml-1" }, "Add a Vehicle"))),
        React.createElement("div", { className: "mt-auto pb-8" }, hasOneVehicle && !someVehicleHasMissingData ? (React.createElement(React.Fragment, null,
            React.createElement("p", { className: "mb-3 text-center text-lg font-bold" },
                "Add Second Vehicle?",
                React.createElement("br", null),
                "(Save Additional 20%)"),
            React.createElement("div", { className: "grid grid-cols-2 gap-4" },
                React.createElement(Button, { onClick: function () {
                        clickedAddButton(__assign(__assign({}, commonAnalyticsProperties), { typeOfEntity: typeOfEntity }));
                        push(PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_YEAR.replace(":vehicleId", uuid()));
                    } }, "Yes"),
                React.createElement(Button, { variant: "outlined", onClick: function (ev) {
                        completedPage(__assign(__assign({}, commonAnalyticsProperties), { selectedOption: ev.currentTarget.textContent, optionSelected: ev.currentTarget.textContent, pageName: PAGES.UNLINKED_APPLICATION_AUTO_VEHICLES_REVIEW }));
                        push(nextRoute);
                    } }, "No")))) : (React.createElement(Button, { disabled: vehicles.length == 0, onClick: function (ev) {
                completedPage(__assign(__assign({}, commonAnalyticsProperties), { selectedOption: ev.currentTarget.textContent, optionSelected: ev.currentTarget.textContent, pageName: PAGES.UNLINKED_APPLICATION_AUTO_VEHICLES_REVIEW }));
                if (someVehicleHasMissingData &&
                    editId &&
                    invalidVehicleEditPage) {
                    push(invalidVehicleEditPage.replace(":vehicleId", editId));
                }
                else {
                    push(nextRoute);
                }
            } }, someVehicleHasMissingData ? "Add Missing Info" : "Continue"))),
        React.createElement(ConfirmationDialog, { title: "Remove Vehicle", message: "Are you sure you want to remove this vehicle? This action cannot be undone.", confirmButtonText: "Remove", cancelButtonText: "Cancel", isOpen: confirmationDialogOpen, onCancel: handleCancelConfirmationDialog, onConfirm: handleRemoveConfirmationDialog, icon: React.createElement(Image, { width: "48", height: "48", src: "/assets/icons/car-blue.svg" }) })));
}
